/**
 * **Async**hronous **coll**ection offers some functions you already know
 * from JSs *array* class, but with support for *Promise*s.
 * @class AsyncColl
 * @example
 * import {asyncColl} from 'js-utils';
 * const values = await asyncColl(urls)
 *   .map(url => loadAsync(url))
 *   .filter(data => data.item > 2)
 *   .values();
 */
export class AsyncColl {
	/**
	 * @param {Promise} p
	 */
	constructor(p) {
		this.promise = p;
	}

	/**
	 * map values
	 * @param {asyncArrayCallback} cb
	 * @param {*} [thisArg] *this* to call *cb* with
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 */
	map(cb, thisArg) {
		const retP = this.promise.then(arr => {
			const ps = arr.map(cb, thisArg);
			return Promise.all(ps);
		});
		return new AsyncColl(retP);
	}

	/**
	 * filter values
	 * @param {asyncArrayCallbackBoolean} cb
	 * @param {*} [thisArg] *this* to call *cb* with
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 */
	filter(cb, thisArg) {
		const retP = this.promise
			.then(arr => {
				const ps = arr.map((item, index, array) =>
					Promise
						.resolve(thisArg::cb(item, index, array))
						.then(res => [res, item])
				);
				return Promise.all(ps);
			})
			.then(items => {
				const ret = [];
				for (let i = 0; i < items.length; ++i) {
					if (items[i][0]) {
						ret.push(items[i][1]);
					}
				}
				return ret;
			});
		return new AsyncColl(retP);
	}

	/**
	 * reverse values
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 */
	reverse() {
		const retP = this.promise.then(arr => arr.slice().reverse());
		return new AsyncColl(retP);
	}

	/**
	 * reduce values
	 * @param {asyncArrayReduceCallback} cb
	 * @param {*} [initVal] initial reduce value
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 * @throws {TypeError} if no *initVal* given and the array is empty
	 */
	reduce(cb, initValArg) {
		return this.promise.then(arr => {
			let i = 0;

			let initVal = initValArg;
			if (!initVal) {
				if (arr.length === 0) {
					throw new TypeError('Empty array given');
				}

				initVal = arr[0];
				i = 1;
			}

			let p = Promise.resolve(initVal);

			for (; i < arr.length; ++i) {
				const curI = i;
				/* eslint no-loop-func: 0 */
				p = p.then(lastVal => Promise.resolve(cb(lastVal, arr[curI], curI, arr)));
			}

			return p;
		});
	}

	/**
	 * reduce right values
	 * @param {asyncArrayReduceCallback} cb
	 * @param {*} [initVal] initial reduce value
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 * @throws {TypeError} if no *initVal* given and the array is empty
	 */
	reduceRight(cb, initVal) {
		return this.reverse().reduce(cb, initVal);
	}

	/**
	 * iterates over values
	 * @param {asyncArrayCallback} cb
	 * @param {*} [thisArg] *this* to call *cb* with
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 */
	forEach(cb, thisArg) {
		const retP = this.promise.then(arr => {
			const ps = arr.map(cb, thisArg);
			return Promise.all(ps).then(() => arr);
		});
		return new AsyncColl(retP);
	}

	/**
	 * tests whether *every* values passes
	 * @param {asyncArrayCallbackBoolean} cb
	 * @param {*} [thisArg] *this* to call *cb* with
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 */
	every(cb, thisArg) {
		return this.promise
			.then(arr => {
				const ps = arr.map(cb, thisArg);
				return Promise.all(ps);
			})
			.then(arr => {
				for (let i = 0; i < arr.length; ++i) {
					if (!arr[i]) {
						return false;
					}
				}
				return true;
			});
	}

	/**
	 * tests whether *some* values passes
	 * @param {asyncArrayCallbackBoolean} cb
	 * @param {*} [thisArg] *this* to call *cb* with
	 * @return {AsyncColl} returns a new instance of *AsyncColl*
	 */
	some(cb, thisArg) {
		return this.promise
			.then(arr => {
				const ps = arr.map(cb, thisArg);
				return Promise.all(ps);
			})
			.then(arr => {
				for (let i = 0; i < arr.length; ++i) {
					if (arr[i]) {
						return true;
					}
				}
				return false;
			});
	}

	/**
	 * returns values
	 * @return {Promise<array<*>>} values
	 */
	values() {
		return this.promise;
	}
}

/**
 * creates an {@link AsyncColl} object.
 * @param {array<*>} arr
 * @return {AsyncColl}
 * @throws {TypeError} if *arr* is not an array
 */
export function asyncColl(arr) {
	if (!Array.isArray(arr)) {
		throw new TypeError(`"${arr}" is not an array`);
	}

	return new AsyncColl(Promise.all(arr));
}
